import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Save, Home, Users, Mail, MessageSquare, DollarSign } from 'lucide-react';
import { getSettings, saveSettings } from '../utils/settings';
import './SecretBackDoor.css';

const SecretBackDoor = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Settings state - initialize with empty object
  const [settings, setSettings] = useState({});

  useEffect(() => {
    // Check if already authenticated
    const auth = sessionStorage.getItem('backdoorAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadSettings();
    } else {
      setLoading(false);
    }
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const fetchedSettings = await getSettings();
      setSettings(fetchedSettings);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'Tom' && password === 'Niyazberdiev2025$$$') {
      setIsAuthenticated(true);
      sessionStorage.setItem('backdoorAuth', 'true');
      setError('');
      loadSettings();
    } else {
      setError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('backdoorAuth');
    sessionStorage.removeItem('cachedSettings');
    setUsername('');
    setPassword('');
  };

  const handleSave = async () => {
    try {
      setSaveMessage('Saving...');
      
      // Save to backend
      await saveSettings(settings);
      
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
      
      // Trigger a custom event to notify other components
      window.dispatchEvent(new Event('settingsUpdated'));
    } catch (error) {
      setSaveMessage('Error saving settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
      console.error('Save error:', error);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="backdoor-login">
        <div className="login-card">
          <div className="login-header">
            <Lock size={48} color="var(--brand-primary)" />
            <h1>Secret Back Door</h1>
            <p>Administrator Access Only</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="btn-primary">
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="backdoor-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Secret Back Door Dashboard</h1>
          <p>Control panel for site content management</p>
        </div>
        <button onClick={handleLogout} className="btn-secondary">
          Logout
        </button>
      </div>

      {saveMessage && (
        <div className="success-message">{saveMessage}</div>
      )}

      <div className="dashboard-content">
        {/* Home Page Hero Section */}
        <div className="settings-section">
          <div className="section-header">
            <Home size={24} />
            <h2>Home Page Hero Section</h2>
          </div>
          
          <div className="form-group">
            <label>Hero Title</label>
            <input
              type="text"
              value={settings.homeHeroTitle}
              onChange={(e) => handleChange('homeHeroTitle', e.target.value)}
              placeholder="Enter hero title"
            />
          </div>

          <div className="form-group">
            <label>Hero Description</label>
            <textarea
              value={settings.homeHeroDescription}
              onChange={(e) => handleChange('homeHeroDescription', e.target.value)}
              placeholder="Enter hero description"
              rows="4"
            />
          </div>
        </div>

        {/* Agents Page Hero Section */}
        <div className="settings-section">
          <div className="section-header">
            <Users size={24} />
            <h2>Agents Page Hero Section</h2>
          </div>
          
          <div className="form-group">
            <label>Hero Title</label>
            <input
              type="text"
              value={settings.agentsHeroTitle}
              onChange={(e) => handleChange('agentsHeroTitle', e.target.value)}
              placeholder="Enter agents page title"
            />
          </div>

          <div className="form-group">
            <label>Hero Description</label>
            <textarea
              value={settings.agentsHeroDescription}
              onChange={(e) => handleChange('agentsHeroDescription', e.target.value)}
              placeholder="Enter agents page description"
              rows="3"
            />
          </div>
        </div>

        {/* Contact Page Settings */}
        <div className="settings-section">
          <div className="section-header">
            <Mail size={24} />
            <h2>Contact Page Settings</h2>
          </div>

          <div className="form-group">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={settings.showContactInfo}
                onChange={(e) => handleChange('showContactInfo', e.target.checked)}
              />
              <span>Show Contact Information</span>
            </label>
          </div>

          {settings.showContactInfo && (
            <>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={settings.contactEmail}
                  onChange={(e) => handleChange('contactEmail', e.target.value)}
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={settings.contactPhone}
                  onChange={(e) => handleChange('contactPhone', e.target.value)}
                  placeholder="Enter phone number"
                />
              </div>
            </>
          )}
        </div>

        {/* Chat Widget Settings */}
        <div className="settings-section">
          <div className="section-header">
            <MessageSquare size={24} />
            <h2>AI Chat Widget Settings</h2>
          </div>

          <div className="form-group">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={settings.showChatWidget}
                onChange={(e) => handleChange('showChatWidget', e.target.checked)}
              />
              <span>Show AI Chat Support Widget</span>
            </label>
          </div>
        </div>

        {/* Pricing Plans Settings */}
        <div className="settings-section">
          <div className="section-header">
            <DollarSign size={24} />
            <h2>Pricing Plans Settings</h2>
          </div>

          {(!settings.pricingPlans || settings.pricingPlans.length === 0) ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <p>No pricing plans configured. Please refresh the page.</p>
            </div>
          ) : (
            settings.pricingPlans.map((plan, index) => (
            <div key={index} className="pricing-plan-editor">
              <h3 style={{ color: 'var(--brand-primary)', marginBottom: '20px', fontSize: '18px' }}>
                Plan {index + 1} {plan.popular && '(Most Popular)'}
              </h3>
              
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Plan Name</label>
                  <input
                    type="text"
                    value={plan.name}
                    onChange={(e) => {
                      const newPlans = [...settings.pricingPlans];
                      newPlans[index].name = e.target.value;
                      handleChange('pricingPlans', newPlans);
                    }}
                    placeholder="e.g., Startup, SME, Enterprise"
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label>Price</label>
                  <input
                    type="text"
                    value={plan.price}
                    onChange={(e) => {
                      const newPlans = [...settings.pricingPlans];
                      newPlans[index].price = e.target.value;
                      handleChange('pricingPlans', newPlans);
                    }}
                    placeholder="e.g., $499, Custom"
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label>Period</label>
                  <input
                    type="text"
                    value={plan.period}
                    onChange={(e) => {
                      const newPlans = [...settings.pricingPlans];
                      newPlans[index].period = e.target.value;
                      handleChange('pricingPlans', newPlans);
                    }}
                    placeholder="e.g., /month, /year"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={plan.description}
                  onChange={(e) => {
                    const newPlans = [...settings.pricingPlans];
                    newPlans[index].description = e.target.value;
                    handleChange('pricingPlans', newPlans);
                  }}
                  placeholder="Brief description of the plan"
                />
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>CTA Button Text</label>
                  <input
                    type="text"
                    value={plan.cta}
                    onChange={(e) => {
                      const newPlans = [...settings.pricingPlans];
                      newPlans[index].cta = e.target.value;
                      handleChange('pricingPlans', newPlans);
                    }}
                    placeholder="e.g., Start Free Trial"
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label>CTA Button Link</label>
                  <input
                    type="text"
                    value={plan.ctaLink}
                    onChange={(e) => {
                      const newPlans = [...settings.pricingPlans];
                      newPlans[index].ctaLink = e.target.value;
                      handleChange('pricingPlans', newPlans);
                    }}
                    placeholder="e.g., /demo, /contact"
                  />
                </div>
              </div>

              {index < settings.pricingPlans.length - 1 && (
                <div style={{ 
                  borderBottom: '1px solid var(--border-subtle)', 
                  margin: '24px 0' 
                }} />
              )}
            </div>
          ))
          )}
        </div>

        {/* Save Button */}
        <div className="save-section">
          <button onClick={handleSave} className="btn-primary btn-save">
            <Save size={20} />
            Save All Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecretBackDoor;
